import React from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import SectionShell from './SectionShell';
import { IFormFieldInputBaseProps } from '@components/form-fields';
import { Tooltip } from '@mui/material';
import { MAIN_THEME } from '@styles';

interface IWidgetSectionProps extends IFormFieldInputBaseProps {
  actions?: JSX.Element;
  middle?: JSX.Element;
  classes?: {
    header?: string;
    headerContainer?: string;
    container?: string;
    middle?: string;
    title?: string;
  };
  title?: string;
  hasTitleSpecificDesign?: boolean;
  specificTitleDesign?: JSX.Element;
  subtitle?: JSX.Element;
  useSeparator?: boolean;
  children?: React.ReactNode;
  showActionButton?: boolean;
  narrowSpacing?: boolean;
  isCollapsible?: boolean;
  collapsibleSectionTitle?: string;
  hideHeaderBorder?: boolean;
  isFieldsGrid?: boolean;
  fieldsPerRow?: number;
}

const useStyles = makeStyles<{
  narrowSpacing: boolean;
  fieldsPerRow: number;
}>()((theme, { narrowSpacing, fieldsPerRow }) => ({
  sectionHeader: {
    display: 'flex',
    alignItems: narrowSpacing ? 'flex-end' : 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    padding: narrowSpacing ? '0 0 0.3em 0' : '0 0 0.5em 0',
  },
  leftHeader: {
    margin: '0 0.5em 0 0',
  },
  sectionTitle: {
    display: 'block',
    minWidth: 'fit-content',
    fontFamily: MAIN_THEME.font.bold,
    fontSize: `${MAIN_THEME.fontSize.lg} !important`,
  },
  subtitleContainer: {
    display: 'block',
    margin: '0.5em 0 0',
  },
  itemsContainer: {
    margin: '0 0 0.5em',
  },
  fieldsGrid: {
    display: 'grid',
    gap: '1em',
    gridTemplateColumns: `repeat(${fieldsPerRow}, 1fr)`,
    alignItems: 'center',
  },
}));

const WidgetSection: React.FC<IWidgetSectionProps> = ({
  actions,
  children,
  classes = {},
  middle,
  title,
  subtitle,
  useSeparator,
  className,
  style,
  showActionButton = true,
  narrowSpacing = false,
  hasTitleSpecificDesign = false,
  specificTitleDesign,
  hideHeaderBorder = false,
  isFieldsGrid = false,
  fieldsPerRow = 4,
}) => {
  const { classes: sectionClasses } = useStyles({
    narrowSpacing,
    fieldsPerRow,
  });

  return (
    <SectionShell
      classes={{
        header: clsx(sectionClasses.sectionHeader, classes.headerContainer),
        container: isFieldsGrid ? sectionClasses.fieldsGrid : classes.container,
      }}
      className={className}
      style={style}
      useSeparator={useSeparator}
      hideHeaderBorder={hideHeaderBorder}
      header={
        title ? (
          <>
            <div className={clsx(sectionClasses.leftHeader, classes.header)}>
              {title.length > 25 ? (
                <Tooltip title={title}>
                  <span
                    className={clsx(sectionClasses.sectionTitle, classes.title)}
                  >
                    {title}
                  </span>
                </Tooltip>
              ) : (
                <span
                  className={clsx(sectionClasses.sectionTitle, classes.title)}
                >
                  {title}
                </span>
              )}
              {!!subtitle && (
                <span className={sectionClasses.subtitleContainer}>
                  {subtitle}
                </span>
              )}
            </div>
            {middle && (
              <div
                className={clsx(sectionClasses.itemsContainer, classes.middle)}
              >
                {middle}
              </div>
            )}
            {actions && showActionButton && (
              <div className={sectionClasses.itemsContainer}>{actions}</div>
            )}
          </>
        ) : hasTitleSpecificDesign ? (
          <>
            <div className={clsx(sectionClasses.leftHeader, classes.header)}>
              <span
                className={clsx(sectionClasses.sectionTitle, classes.title)}
              >
                {specificTitleDesign}
              </span>
              {!!subtitle && (
                <span className={sectionClasses.subtitleContainer}>
                  {subtitle}
                </span>
              )}
            </div>
            {middle && (
              <div
                className={clsx(sectionClasses.itemsContainer, classes.middle)}
              >
                {middle}
              </div>
            )}
            {actions && showActionButton && (
              <div className={sectionClasses.itemsContainer}>{actions}</div>
            )}
          </>
        ) : undefined
      }
    >
      {children}
    </SectionShell>
  );
};

export default WidgetSection;
